import { Snackbar, SnackbarHandleImpl } from './Snackbar'
import { action, observable } from 'mobx'

import { extractMessageFromError } from '@/utils/error-util'
import type {
  SnackbarRetryWrapperFactory,
  SnackbarErrorHandlerFactory,
  SnackbarFactory,
} from './'
import { firstValueFrom, map, race, Subject } from 'rxjs'
import { fromAbortSignal } from '@/utils/rx-util'
import { tap } from 'rxjs/operators'
import { isAbortError } from '@/utils/abort-util'

/**
 * Manages snackbars.
 */
export class SnackbarsManager {
  /**
   * The visible snackbars.
   */
  @observable accessor snackbars: Snackbar[] = []

  /**
   * The latest snackbar.
   */
  @observable accessor latest: Snackbar | null = null

  /**
   * Creates a new Snackbar.
   *
   * @param configure Configures the snackbar inside the action.
   */
  @action.bound
  newSnackbar(configure?: (snackbar: Snackbar) => unknown): Snackbar {
    const snackbar: Snackbar = new Snackbar(
      () => this.addSnackbar(snackbar),
      () => this.removeSnackbar(snackbar),
    )

    configure?.(snackbar)

    return snackbar
  }

  /**
   * Adds the snackbar to the list.
   *
   * @param snackbar
   */
  @action.bound
  private addSnackbar(snackbar: Snackbar) {
    if (snackbar.key) {
      const existing = this.snackbars.find((s) => s.key === snackbar.key)
      if (existing) {
        existing.dismiss()
      }
    }

    this.snackbars.push(snackbar)
    this.latest = snackbar
  }

  /**
   * Removes the snackbar from the list.
   *
   * @param snackbar
   */
  @action.bound
  private removeSnackbar(snackbar: Snackbar) {
    const idx = this.snackbars.indexOf(snackbar)
    if (idx === -1) {
      return
    }

    this.snackbars.splice(idx, 1)
    if (this.latest === snackbar) {
      this.latest = null
    }
  }
}

/**
 * Creates a snackbar factory. Delicious!
 *
 * @param manager
 */
export function createSnackbarFactory(
  manager: SnackbarsManager,
): SnackbarFactory {
  return action('newSnackbar', (configure) => {
    const snackbar = manager.newSnackbar()
    const handle = new SnackbarHandleImpl(snackbar)
    configure?.(handle)
    return handle
  })
}

/**
 * Creates a snackbar error handler.
 *
 * @param manager
 */
export function createSnackbarErrorHandlerFactory(
  manager: SnackbarsManager,
): SnackbarErrorHandlerFactory {
  return (title: string) =>
    action('snackbarErrorHandler', function snackbarErrorHandler(err: unknown) {
      const snackbar = manager.newSnackbar()
      snackbar
        .setType('error')
        .saying(title, extractMessageFromError(err))
        .autoDismiss(20_000)
        .show()
      throw err
    })
}

/**
 * Creates a snackbar retry wrapper factory.
 * The wrapper function will not complete until either the call is successful,
 * or the snackbar is dismissed without retrying, in which case the
 * last error is thrown.
 *
 * @param manager
 */
export function createSnackbarRetryWrapperFactory(
  manager: SnackbarsManager,
): SnackbarRetryWrapperFactory {
  return (title, signal) => async (fn) => {
    while (true) {
      signal?.throwIfAborted()
      try {
        return await fn()
      } catch (err: unknown) {
        // If the abort signal has triggered, or it was an abort error,
        // just throw the error now.
        if (signal?.aborted || isAbortError(err)) {
          throw err
        }

        const retry = await shouldRetry(err)
        if (!retry) {
          throw err
        }
      }
    }

    /**
     * Handles the error by showing a snackbar and waiting for a retry signal.
     *
     * @param err The error.
     * @return `true` if the error should be retried;
     *  `false` if the error should be thrown and the retry loop aborted;
     */
    async function shouldRetry(err: unknown): Promise<boolean> {
      const retry$ = new Subject<boolean>()
      const snackbar = manager.newSnackbar((s) =>
        s
          .setType('error')
          .saying(title, extractMessageFromError(err))
          .setActions({
            title: 'Try again',
            handler() {
              retry$.next(true)
            },
          })
          .show(),
      )

      // Wait for either a dismissal or a retry.
      // When dismissing, we want to return `false` since we want the error
      // to be thrown by the caller; if the action handler is triggered,
      // we want to return `true`.
      return await firstValueFrom(
        race(
          retry$,
          snackbar.dismissed$.pipe(map(() => false)),
          // Runs the retry loop again but the signal is checked immediately.
          fromAbortSignal(signal).pipe(map(() => true)),
        ).pipe(
          // Dismiss the snackbar regardless of the outcome.
          tap(() => snackbar.dismiss()),
        ),
      )
    }
  }
}
